import type { SubscriptionStatus, SectorPlanFeatures } from "@/types";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { useAuth } from "@/core/hooks/useAuth";
import { baseQueryOptions } from "@/core/hooks/useApiQueries/baseQueryOptions";
import { apiQueryFn } from "@/core/hooks/useApiQueries/useApiQuery";

/**
 * Modo de entrada snapshotado na subscription. `direct_payment` (SA-1218) é o
 * link de vendas **sem trial**: mesmo gate duro do `with_card`, mas a cobrança
 * sai no ato em que o cartão é anexado. O front precisa distinguir os dois para
 * não prometer dias grátis a quem vai pagar agora.
 */
export type OnboardingMode = "without_card" | "with_card" | "direct_payment";

/**
 * Como a Company é cobrada (SA-1298). `partner_free` e `external` liberam o
 * produto sem assinatura paga no Stripe; `stripe` e `null` seguem o caminho
 * normal de cobrança.
 */
export type BillingArrangement = "stripe" | "partner_free" | "external";

export type SectorAccessState = {
  enforcement_enabled: boolean;
  is_internal: boolean;
  billing_arrangement: BillingArrangement | null;
  subscription_status: SubscriptionStatus | null;
  trial_ends_at: string | null;
  /** Vencimento decidido pelo relógio do servidor, nunca por `Date.now()`. */
  trial_expired: boolean;
  onboarding_mode: OnboardingMode | null;
  has_payment_method: boolean;
  /**
   * SA-1371: a cobrança que libera a conta foi recusada e a fatura segue em
   * aberto. Cartão salvo não é cobrança paga: no `direct_payment` o produto só
   * abre com o dinheiro dentro.
   */
  activation_payment_failed: boolean;
  /** Plano vigente, para a tela de recusa pré-selecionar o que foi comprado. */
  plan_slug: string | null;
  /**
   * A conta paga o preço de LISTA do plano (o servidor compara o price da
   * assinatura com o mensal e o anual do plano visível com preço). O bloqueio
   * por plano só nomeia um plano mínimo com isto `true`.
   */
  plan_price_verified: boolean;
  plan_features: SectorPlanFeatures | null;
};

// Fail-open (BIL-69): durante loading/erro do endpoint, o default desativa o
// enforcement — nunca bloqueamos ninguém silenciosamente. Se o endpoint está
// fora do ar, o produto inteiro está com problema; manter o kill switch
// "desligado" preserva o propósito de não derrubar clientes.
const FAIL_OPEN_DEFAULT: SectorAccessState = {
  enforcement_enabled: false,
  is_internal: false,
  billing_arrangement: null,
  subscription_status: null,
  trial_ends_at: null,
  trial_expired: false,
  onboarding_mode: null,
  has_payment_method: false,
  activation_payment_failed: false,
  plan_slug: null,
  plan_price_verified: false,
  plan_features: null,
};

/**
 * Lê o estado de acesso de billing do sector ativo via
 * `GET /api/billing/access-state/:sectorId` — endpoint server-side que devolve
 * `enforcement_enabled` + `is_internal` + `subscription_status` + `trial_ends_at`
 * com leitura privilegiada (a subscription populada no `/sectors/:id` é removida
 * pela sanitização de RBAC, então não dá pra confiar nela no frontend).
 */
export const sectorAccessStateQueryKey = (
  sectorId: string | null | undefined
) => ["billing", "access-state", sectorId ?? "__none__"] as const;

/** Estado de acesso do setor, para o hook e para o boot (SA-1452). */
export const sectorAccessStateQueryOptions = (
  sectorId: string | null | undefined,
  token: string | null | undefined
) =>
  queryOptions({
    ...baseQueryOptions,
    queryKey: sectorAccessStateQueryKey(sectorId),
    queryFn: apiQueryFn<SectorAccessState>(
      token,
      "GET",
      sectorAccessStateQueryKey(sectorId)
    ),
    staleTime: 60_000,
  });

export function useSectorAccessState(sectorId: string | null | undefined) {
  const { token } = useAuth();
  const query = useQuery({
    ...sectorAccessStateQueryOptions(sectorId, token),
    enabled: Boolean(sectorId),
  });

  const data: SectorAccessState = query.data ?? FAIL_OPEN_DEFAULT;

  return { ...query, data };
}
